'use client'

import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, HelpCircle, MessageCircle, Phone, Search } from 'lucide-react'
import { useState } from 'react'
import Link from 'next/link'
import faqs from '@/data/faqs.json'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'

const categoryIcons: Record<string, string> = {
  'Company Formation': '🏢',
  'Golden Visa': '🏅',
  'Attestation': '📋',
  'Notary': '✍️',
  'Power of Attorney': '⚖️',
  'Property': '🏠',
}

const categoryColors: Record<string, { bg: string; activeBg: string; activeText: string; border: string }> = {
  'Company Formation': { bg: 'bg-white', activeBg: '#EFF6FF', activeText: '#1D4ED8', border: '#DBEAFE' },
  'Golden Visa': { bg: 'bg-white', activeBg: '#FFFBEB', activeText: '#D97706', border: '#FDE68A' },
  'Attestation': { bg: 'bg-white', activeBg: '#F0FDF4', activeText: '#059669', border: '#A7F3D0' },
  'Notary': { bg: 'bg-white', activeBg: '#FAF5FF', activeText: '#7C3AED', border: '#DDD6FE' },
  'Power of Attorney': { bg: 'bg-white', activeBg: '#FFF1F2', activeText: '#E11D48', border: '#FECDD3' },
  'Property': { bg: 'bg-white', activeBg: '#F0F9FF', activeText: '#0891B2', border: '#BAE6FD' },
}

export function FAQSection() {
  const { ref, isVisible } = useScrollAnimation()
  const [expanded, setExpanded] = useState<number | null>(1)
  const [activeCategory, setActiveCategory] = useState<string>('All')

  const categories = ['All', ...Array.from(new Set(faqs.faqs.map((faq) => faq.category)))]

  const filteredFaqs =
    activeCategory === 'All' ? faqs.faqs : faqs.faqs.filter((faq) => faq.category === activeCategory)

  return (
    <section id="faq" ref={ref} className="py-20 md:py-28 bg-white">
      <div className="container-narrow max-w-4xl">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <span className="section-badge">
            <HelpCircle className="w-4 h-4" />
            FAQs
          </span>
          <h2 className="section-title">Frequently Asked Questions</h2>
          <p className="section-subtitle max-w-xl mx-auto">
            Find quick answers to common questions about our document clearing and government services in UAE.
          </p>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex flex-wrap gap-2 justify-center mb-10"
        >
          {categories.map((cat) => {
            const isActive = activeCategory === cat
            const colors = cat !== 'All' ? categoryColors[cat] : null
            return (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 flex items-center gap-1.5 ${
                  isActive
                    ? 'shadow-soft text-white'
                    : 'bg-white border border-[#DBEAFE] text-[#475569] hover:border-[#1D4ED8] hover:text-[#1D4ED8]'
                }`}
                style={
                  isActive
                    ? { background: 'linear-gradient(135deg, #1D4ED8 0%, #3B82F6 100%)' }
                    : {}
                }
              >
                {cat !== 'All' && categoryIcons[cat] && (
                  <span>{categoryIcons[cat]}</span>
                )}
                {cat}
              </button>
            )
          })}
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-3 mb-14">
          {filteredFaqs.map((faq, idx) => (
            <motion.div
              key={faq.id}
              initial={{ opacity: 0, y: 10 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: idx * 0.04 }}
              className="bg-white rounded-2xl border border-[#DBEAFE] overflow-hidden hover:border-[#93C5FD] transition-colors duration-300"
              style={{ boxShadow: '0 2px 8px rgba(29,78,216,0.05)' }}
            >
              <button
                onClick={() => setExpanded(expanded === faq.id ? null : faq.id)}
                className="w-full px-6 py-4 flex items-start justify-between hover:bg-[#F8FBFF] transition-colors duration-200 text-left"
                aria-expanded={expanded === faq.id}
              >
                <div className="flex-1 pr-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm">
                      {categoryIcons[faq.category] || '❓'}
                    </span>
                    <span className="text-xs font-semibold text-[#1D4ED8] uppercase tracking-wide">
                      {faq.category}
                    </span>
                  </div>
                  <h3 className="text-base md:text-lg font-semibold text-[#1E293B]">
                    {faq.question}
                  </h3>
                </div>
                <div
                  className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                    expanded === faq.id ? 'bg-[#1D4ED8] text-white' : 'bg-[#EFF6FF] text-[#1D4ED8]'
                  }`}
                >
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-300 ${
                      expanded === faq.id ? 'rotate-180' : ''
                    }`}
                  />
                </div>
              </button>

              <AnimatePresence>
                {expanded === faq.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="border-t border-[#DBEAFE]"
                  >
                    <div className="px-6 py-5">
                      <p className="text-[#475569] leading-relaxed text-sm md:text-base">
                        {faq.answer}
                      </p>
                      <div className="mt-4 flex items-center gap-3">
                        <Link
                          href="/contact"
                          className="inline-flex items-center gap-1.5 text-[#1D4ED8] text-sm font-semibold hover:text-[#1E40AF] transition-colors duration-200"
                        >
                          <MessageCircle className="w-4 h-4" />
                          Ask a specific question →
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Still Have Questions CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="rounded-3xl p-8 md:p-10 text-center"
          style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)', border: '1px solid #BFDBFE' }}
        >
          <div className="w-16 h-16 rounded-2xl bg-white shadow-soft text-[#1D4ED8] flex items-center justify-center mx-auto mb-4">
            <HelpCircle className="w-8 h-8" />
          </div>
          <h3 className="text-2xl font-bold text-[#1E293B] mb-2">Still Have Questions?</h3>
          <p className="text-[#475569] mb-8 max-w-md mx-auto">
            Our expert team is ready to answer any questions about your specific situation.
            Reach out for a free consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#1D4ED8] text-white rounded-xl font-bold hover:bg-[#1E40AF] transition-all duration-300 shadow-soft hover:-translate-y-0.5"
            >
              <MessageCircle className="w-5 h-5" />
              Contact Our Team
            </Link>
            <a
              href="tel:+97141234567"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-[#1D4ED8] text-[#1D4ED8] rounded-xl font-bold hover:bg-white transition-all duration-300"
            >
              <Phone className="w-5 h-5" />
              Call Us Now
            </a>
          </div>
        </motion.div>

      </div>
    </section>
  )
}
